import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { fetchYouTubeData } from '@/lib/youtube-service';

export interface TopicVideo {
  id: string;
  topic_id: string;
  user_id: string;
  video_id: string;
  title: string;
  channel_title?: string;
  description?: string;
  duration?: string;
  embed_url: string;
  thumbnail_url?: string;
  transcript?: any[];
  chapters?: any[];
  has_chapters: boolean;
  full_text?: string;
  language: string;
  total_segments: number;
  video_info?: any;
  created_at: string;
  updated_at: string;
}

export const useTopicVideos = (topicId: string | undefined) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['topicVideos', topicId, user?.id],
    queryFn: async () => {
      if (!topicId || !user?.id) return [];

      const { data, error } = await supabase
        .from('topic_videos')
        .select('*')
        .eq('topic_id', topicId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching topic videos:', error);
        throw error;
      }

      return data as TopicVideo[];
    },
    enabled: !!topicId && !!user?.id,
  });
};

export const useAddTopicVideo = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      topicId, 
      videoId, 
      embedUrl 
    }: { 
      topicId: string; 
      videoId: string; 
      embedUrl: string; 
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Fetch YouTube data including transcript and chapters
      const youtubeData = await fetchYouTubeData(videoId);
      
      if (!youtubeData.video_info) {
        throw new Error('Failed to fetch video information');
      }

      // Prepare full text for search
      const fullText = youtubeData.transcript
        ?.map(item => item.text)
        .join(' ') || '';

      const videoData = {
        topic_id: topicId,
        user_id: user.id,
        video_id: videoId,
        title: youtubeData.video_info.title,
        channel_title: youtubeData.video_info.title || '',
        embed_url: embedUrl,
        thumbnail_url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
        transcript: youtubeData.transcript as any || null,
        chapters: youtubeData.chapters as any || null,
        has_chapters: (youtubeData.chapters?.length || 0) > 0,
        full_text: fullText,
        language: 'en',
        total_segments: youtubeData.transcript?.length || 0,
        video_info: youtubeData.video_info as any,
      };

      const { data, error } = await supabase
        .from('topic_videos')
        .insert(videoData)
        .select()
        .single();

      if (error) {
        console.error('Error adding topic video:', error);
        throw error;
      }

      return data as TopicVideo;
    },
    onSuccess: (data) => {
      // Invalidate and refetch topic videos
      queryClient.invalidateQueries({ 
        queryKey: ['topicVideos', data.topic_id] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['topic-library-items', data.topic_id] 
      });
    },
  });
};

export const useDeleteTopicVideo = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, topicId }: { id: string; topicId: string }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('topic_videos')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting topic video:', error);
        throw error;
      }

      return { id, topicId };
    },
    onSuccess: (data) => {
      // Invalidate and refetch topic videos
      queryClient.invalidateQueries({ 
        queryKey: ['topicVideos', data.topicId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['topic-library-items', data.topicId] 
      });
    },
  });
};